import { type ReactNode } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { Card, CardContent } from "../components/ui/card";
import { Skeleton } from "../components/ui/skeleton";
import { PageHeader } from "../components/ui/page-header";
import { GroupSettingsPanel } from "../components/GroupSettingsPanel";
import { useGroup, useMe } from "../api/hooks";
import { canManageGroup, getMyGroupRole } from "../lib/groupRole";

export function GroupSettingsPage(): ReactNode {
  const { groupId } = useParams<{ groupId: string }>();
  const navigate = useNavigate();
  const groupQ = useGroup(groupId);
  const meQ = useMe();

  if (groupQ.isLoading) {
    return (
      <div className="mx-auto max-w-xl space-y-4">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-64" />
      </div>
    );
  }
  if (!groupQ.data) {
    return (
      <Card tone="ghost">
        <CardContent className="py-12 text-center text-on-surface-variant">
          Group not found.
        </CardContent>
      </Card>
    );
  }

  const group = groupQ.data;
  const myRole = getMyGroupRole(group, meQ.data);
  // Settings are owner-only; bounce everyone else back to the group page.
  if (!canManageGroup(myRole)) {
    return <Navigate to={`/groups/${group.id}`} replace />;
  }

  return (
    <div className="mx-auto max-w-xl space-y-stack-lg">
      <PageHeader
        back={{
          label: group.label,
          onClick: () => navigate(`/groups/${group.id}`),
        }}
        eyebrow="Group settings"
        title={group.label}
      />
      <GroupSettingsPanel group={group} />
    </div>
  );
}
